import { Table } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { CustomPopup, CustomTable, Filter, FilterTag } from "../../../../../components";
import { useGetOpenedGameCategory } from "../../../../../hooks/category.hooks";
import { moneyFormat } from "../../../../../plugins/numberFormat";
import { envChangeType, getCurrency } from "../../../../../plugins/utils";
import { filterKeyGameData } from "../../enumeration/filterKeyEnum";
import { filterSetupGameData } from "../../enumeration/filterSetupEnum";
import { useGetOpenedGames, useGetProductReport } from "../../hooks/productreport.hook";
import ReportDetail from "../ReportDetail";

function Report({ activeTab }) {
  const [totalSum, setTotalSum] = useState(0);
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [selectedSettledAt, setSelectedSettledAt] = useState(null);
  const [detailTitle, setDetailTitle] = useState("");
  const { t } = useTranslation();
  const cache = useQueryClient();
  const nowCurrency = getCurrency();
  const [tableData, setTableData] = useState([]);

  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyGameData.GAME_ID]: null,
    [filterKeyGameData.CATEGORY_ID]: null,
    [filterKeyGameData.SETTLED_AT_START]: dayjs()
      .subtract(1, "months")
      .format("YYYY-MM-DD 00:00:00"),
    [filterKeyGameData.SETTLED_AT_END]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });
  const [filterValue, setFilterValue] = useState({
    [filterKeyGameData.GAME_ID]: null,
    [filterKeyGameData.CATEGORY_ID]: null,
    [filterKeyGameData.SETTLED_AT_START]: dayjs()
      .subtract(1, "months")
      .format("YYYY-MM-DD 00:00:00"),
    [filterKeyGameData.SETTLED_AT_END]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });

  const [dependency, setDependency] = useState({});

  const [gameValues, setGameValues] = useState([]);
  const [gameCategoryValues, setGameCategoryValues] = useState([]);

  const { data: categoryList } = useGetOpenedGameCategory({});
  const { data: gameList } = useGetOpenedGames({});

  const onOpenDetail = (settled_at) => {
    const title = `${settled_at}`;
    setSelectedSettledAt(settled_at);
    setOpenDetailDrawer(true);
    setDetailTitle(title);
  };

  const resetDrawer = () => {
    setDetailTitle("");
    setSelectedSettledAt(null);
  };
  const getProductReport = useGetProductReport({
    options: {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
      enabled:
        filterValue[filterKeyGameData.CATEGORY_ID] !== null &&
        filterValue[filterKeyGameData.GAME_ID] !== null,
    },
    params: {
      settled_at_start: filterValue[filterKeyGameData.SETTLED_AT_START],
      settled_at_end: filterValue[filterKeyGameData.SETTLED_AT_END],
      page_size: 20,
    },
    categoryId: filterValue[filterKeyGameData.CATEGORY_ID],
    gameId: filterValue[filterKeyGameData.GAME_ID],
  });

  const columns = [
    {
      title: t("normal.date"),
      width: 150,
      dataIndex: "trans_date",
      align: "center",
      render: (_, record) => (
        <a
          onClick={() => {
            onOpenDetail(record.trans_date);
          }}
        >
          {record.trans_date}
        </a>
      ),
    },
    {
      title: t("mobile.productReport.winlost", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      width: 150,
      dataIndex: "sum_win_loss",
      align: "center",
      render: (_, record) => (
        <span
          style={{
            color: record?.sum_win_loss >= 0 ? "var(--table-text-green)" : "var(--table-text-red)",
          }}
        >
          {moneyFormat(record?.sum_win_loss)}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (getProductReport.data) {
      const page_length = getProductReport.data.pages.length;
      if (page_length <= 1) {
        setTableData([...getProductReport.data.pages[0].data]);
        setTotalSum(getProductReport?.data?.pages[0]?.total_sum_win_loss);
      } else {
        setTableData((prevState) => {
          return [...prevState, ...getProductReport.data.pages[page_length - 1].data];
        });
      }
    }
  }, [getProductReport.data]);

  useEffect(() => {
    if (categoryList && gameList) {
      const _categoryValues = categoryList.map((category) => {
        return {
          value: category.game_category_id,
          label: `game.category.${category.game_category_code}`,
          is_translation: true,
        };
      });
      const _gameValues = gameList.map((game) => {
        return {
          value: game.game_3th_id,
          category_id: game.game_category_id,
          label: [
            `normal.game.${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.${game.game_3th_code}`,
            `normal.game.${game.game_3th_code}`,
          ],
          is_translation: true,
        };
      });
      const selectedDefaultGameList = _gameValues.filter(
        (game) => game.category_id === _categoryValues[0].value,
      );
      setGameCategoryValues(_categoryValues);
      setGameValues(_gameValues);
      setFilterDefaultValue((prevState) => {
        return {
          ...prevState,
          [filterKeyGameData.CATEGORY_ID]: _categoryValues[0].value,
          [filterKeyGameData.GAME_ID]: selectedDefaultGameList[0].value,
        };
      });
      setFilterValue((prevState) => {
        return {
          ...prevState,
          [filterKeyGameData.CATEGORY_ID]: _categoryValues[0].value,
        };
      });
      setDependency((prevState) => {
        return {
          ...prevState,
          [filterKeyGameData.CATEGORY_ID]: _categoryValues[0].value,
        };
      });
    }
  }, [gameList, categoryList]);

  useEffect(() => {
    if (dependency !== {}) {
      const selectedGameList = gameValues.filter(
        (game) => game.category_id === dependency[filterKeyGameData.CATEGORY_ID],
      );
      if (selectedGameList && selectedGameList.length > 0) {
        setFilterValue({
          ...filterValue,
          ...dependency,
          [filterKeyGameData.GAME_ID]: selectedGameList[0].value,
        });
      }
    }
  }, [dependency, gameValues]);

  const onHandleSearch = () => {
    // cache.invalidateQueries([queryKey.PRODUCT_REPORT]);
    // console.log("filterValue", filterValue);
  };

  return (
    <div>
      <Filter
        filterSetup={filterSetupGameData}
        filterDefaultValue={filterDefaultValue}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
          setFilterValue(filterDefaultValue);
          Object.keys(dependency).forEach((key) => {
            setDependency((prevState) => {
              return {
                ...prevState,
                [key]: filterDefaultValue[key],
              };
            });
          });
        }}
        onSubmitSearch={onHandleSearch}
        setDependency={setDependency}
        dependency={dependency}
        apiValue={{
          [filterKeyGameData.CATEGORY_ID]: {
            value_enum: gameCategoryValues,
          },
          [filterKeyGameData.GAME_ID]: {
            value_enum: gameValues.filter(
              (game) => game.category_id === dependency[filterKeyGameData.CATEGORY_ID],
            ),
          },
        }}
        activeTab={activeTab}
      />
      <FilterTag
        filterSetup={filterSetupGameData}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        refreshSearch={onHandleSearch}
        apiValue={{
          [filterKeyGameData.CATEGORY_ID]: {
            value_enum: gameCategoryValues,
          },
          [filterKeyGameData.GAME_ID]: {
            value_enum: gameValues.filter(
              (game) => game.category_id === filterValue[filterKeyGameData.CATEGORY_ID],
            ),
          },
        }}
      />
      {getProductReport.isLoading ? (
        ""
      ) : getProductReport.isError ? (
        ""
      ) : (
        <CustomTable
          columns={columns}
          dataSource={tableData}
          hasMore={getProductReport.hasNextPage}
          loadMore={async () => {
            if (getProductReport.isFetchingNextPage === false && tableData.length > 0) {
              await getProductReport.fetchNextPage();
            }
          }}
          showInifineScroll={true}
          summary={() => {
            return (
              <Table.Summary fixed="top">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} align="center">
                    <span>{t("normal.summary")}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <span style={{ color: "#EFAD5B" }}>{totalSum ?? "-"}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
      <CustomPopup
        visible={openDetailDrawer}
        onClose={() => {
          setOpenDetailDrawer(false);
        }}
        afterClose={resetDrawer}
        title={detailTitle}
        destroyOnClose={true}
        forceRender={true}
      >
        <ReportDetail
          settledAt={selectedSettledAt}
          gameId={filterValue[filterKeyGameData.GAME_ID]}
          categoryId={filterValue[filterKeyGameData.CATEGORY_ID]}
        />
      </CustomPopup>
    </div>
  );
}

export default Report;
